import { formatBRL } from '@/lib/format';

/**
 * Indique e Ganhe — utilitários do lado do app.
 *
 * - Código PENDENTE em localStorage (`bow-pending-ref-v1`): chega pela landing
 *   `/convite/:code` ou pelo campo opcional do cadastro e sobrevive ao
 *   login/cadastro (o `location.state.from` do router só guarda `pathname` —
 *   uma `?ref=` se perderia). Quem aplica de fato é o `<ReferralClaimer />`
 *   no AppLayout, depois do login, num só lugar (cobre email/Google/Apple).
 *   TTL de 14 dias — depois disso não faz sentido aplicar.
 * - Compartilhar: `navigator.share` onde existe (iOS WKWebView, Chrome);
 *   fallback WhatsApp por `wa.me/?text=` (WebView Android não tem share) e,
 *   em último caso, clipboard.
 */

const PENDING_KEY = 'bow-pending-ref-v1';
const PENDING_TTL_MS = 14 * 24 * 60 * 60 * 1000;

interface PendingRef {
  code: string;
  savedAt: number;
}

/** Código como o cliente digitou/colou → forma compacta (`ANA7K2`) ou null.
 *  O backend é a autoridade (faz a mesma normalização); aqui é só pra não
 *  guardar lixo e pra comparar. */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const compact = raw
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (compact.length < 4 || compact.length > 14) return null;
  return compact;
}

export function setPendingReferralCode(code: string): void {
  try {
    const payload: PendingRef = { code, savedAt: Date.now() };
    localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    /* storage indisponível (modo privado) — segue sem pendente */
  }
}

export function getPendingReferralCode(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingRef>;
    if (!parsed || typeof parsed.code !== 'string' || typeof parsed.savedAt !== 'number') {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    if (Date.now() - parsed.savedAt > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
}

export function clearPendingReferralCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** Mensagem que vai no WhatsApp / share sheet. O link já carrega o código. */
export function inviteMessage(args: {
  code: string;
  link: string;
  welcomeBrl: string | number;
}): string {
  return (
    `Carrego meu elétrico na Bow e tô te indicando: usa meu código ${args.code} ` +
    `no app e ganha ${formatBRL(args.welcomeBrl)} de cashback no seu primeiro aporte. ` +
    `${args.link}`
  );
}

export function whatsappShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function canNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/** Share sheet nativo → (se não houver) clipboard. O botão de WhatsApp é
 *  renderizado como âncora `wa.me` separada quando não há share nativo, pra
 *  o gesto viajar na própria âncora (WebView Android). */
export async function shareInvite(message: string): Promise<ShareOutcome> {
  if (canNativeShare()) {
    try {
      await navigator.share({ title: 'Convite Bow', text: message });
      return 'shared';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return 'cancelled';
      // share indisponível de verdade → cai pro clipboard
    }
  }
  return (await copyText(message)) ? 'copied' : 'failed';
}
